import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { fmtDate, ReasonDialog, type PlatformCall } from './common';

export interface SignupRow {
  id: string;
  reference: string;
  status: string;
  method: 'PASSWORD' | 'GOOGLE';
  company_name: string;
  workspace_key: string;
  tenant_key: string | null;
  country_code: string;
  admin_full_name: string;
  admin_email: string;
  admin_username: string;
  email_verified: boolean;
  requester_ip: string | null;
  user_agent: string | null;
  provisioning_request_id: string | null;
  decided_by: string | null;
  decided_at: string | null;
  decision_reason: string | null;
  error_summary: string | null;
  created_at: string | null;
  steps?: { key: string; label: string; status: string; detail: string | null; error: string | null }[];
}

const STATUS: Record<string, { label: string; color: 'default' | 'warning' | 'success' | 'error' | 'info' }> = {
  PENDING_VERIFICATION: { label: 'Awaiting email', color: 'default' },
  PENDING_APPROVAL: { label: 'Needs approval', color: 'warning' },
  PROVISIONING: { label: 'Creating', color: 'info' },
  COMPLETED: { label: 'Workspace created', color: 'success' },
  FAILED: { label: 'Setup failed', color: 'error' },
  REJECTED: { label: 'Rejected', color: 'default' },
  EXPIRED: { label: 'Expired', color: 'default' },
};

const FILTERS: [string, string][] = [
  ['', 'All'], ['PENDING_APPROVAL', 'Needs approval'], ['PENDING_VERIFICATION', 'Awaiting email'],
  ['PROVISIONING', 'Creating'], ['COMPLETED', 'Created'], ['FAILED', 'Failed'],
  ['REJECTED', 'Rejected'], ['EXPIRED', 'Expired'],
];

/**
 * Self-service signups. In APPROVAL mode this is where a workspace request
 * waits for a person: approving it is what allows its database to be created.
 */
export function SignupsTab({
  call,
  focusId,
  onChanged,
}: {
  call: PlatformCall;
  focusId?: string | null;
  onChanged: () => void;
}) {
  const [rows, setRows] = useState<SignupRow[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [filter, setFilter] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<SignupRow | null>(null);
  const [detail, setDetail] = useState<SignupRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const body = (await call(`/signups${filter ? `?status=${filter}` : ''}`)) as {
        signups: SignupRow[];
        counts: Record<string, number>;
      };
      setRows(body.signups);
      if (!filter) setCounts(body.counts);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [call, filter]);

  useEffect(() => { void load(); }, [load]);

  // Keep the list live while something is being created.
  useEffect(() => {
    if (!rows?.some((r) => r.status === 'PROVISIONING')) return;
    const t = window.setTimeout(() => void load(), 3000);
    return () => window.clearTimeout(t);
  }, [rows, load]);

  const openDetail = useCallback(async (id: string) => {
    try {
      setDetail((await call(`/signups/${id}`)) as SignupRow);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [call]);

  useEffect(() => {
    if (focusId) void openDetail(focusId);
  }, [focusId, openDetail]);

  const act = async (row: SignupRow, action: 'approve' | 'retry') => {
    setBusyId(row.id);
    setNote(null);
    try {
      await call(`/signups/${row.id}/${action}`, { method: 'POST' });
      setNote(action === 'approve'
        ? `Approved ${row.company_name} — its workspace is being created.`
        : `Retrying ${row.company_name} from the step that failed.`);
      await load();
      onChanged();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (err && !rows) return <Alert severity="error">{err}</Alert>;
  if (!rows) return <CircularProgress size={24} />;

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        {FILTERS.map(([value, label]) => (
          <Chip
            key={value || 'all'}
            label={value && counts[value] ? `${label} (${counts[value]})` : label}
            color={filter === value ? 'primary' : value === 'PENDING_APPROVAL' && counts[value] ? 'warning' : 'default'}
            variant={filter === value ? 'filled' : 'outlined'}
            onClick={() => setFilter(value)}
          />
        ))}
      </Stack>
      {note && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNote(null)}>{note}</Alert>}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Company</TableCell>
              <TableCell>Workspace ID</TableCell>
              <TableCell>Administrator</TableCell>
              <TableCell>Method</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    No signups{filter ? ' in this state' : ' yet'}.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => {
              const s = STATUS[r.status] ?? { label: r.status, color: 'default' as const };
              return (
                <TableRow key={r.id} hover selected={r.id === focusId}>
                  <TableCell>
                    <Typography variant="body2">{r.company_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{r.reference} · {r.country_code}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.tenant_key ?? r.workspace_key}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.admin_full_name} ({r.admin_username})</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.admin_email}{r.email_verified ? ' · verified' : ' · not verified'}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.method === 'GOOGLE' ? 'Google' : 'Password'}</TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" label={s.label} color={s.color} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{fmtDate(r.created_at)}</Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {r.status === 'PENDING_APPROVAL' && (
                      <Button size="small" variant="contained" disabled={busyId === r.id}
                        onClick={() => void act(r, 'approve')}>Approve</Button>
                    )}
                    {r.status === 'FAILED' && r.provisioning_request_id && (
                      <Button size="small" disabled={busyId === r.id}
                        onClick={() => void act(r, 'retry')}>Retry</Button>
                    )}
                    {['PENDING_VERIFICATION', 'PENDING_APPROVAL', 'FAILED'].includes(r.status) && !r.tenant_key && (
                      <Button size="small" color="error" onClick={() => setRejecting(r)}>Reject</Button>
                    )}
                    <Button size="small" onClick={() => void openDetail(r.id)}>Details</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <ReasonDialog
        open={!!rejecting}
        title={`Reject ${rejecting?.company_name ?? ''}?`}
        description="No workspace is created. If the email was verified, the requester is told the reason."
        confirmLabel="Reject"
        danger
        onClose={() => setRejecting(null)}
        onConfirm={async (reason) => {
          if (!rejecting) return;
          await call(`/signups/${rejecting.id}/reject`, {
            method: 'POST', body: JSON.stringify({ reason, notify_requester: true }),
          });
          setNote(`Rejected ${rejecting.company_name}.`);
          await load();
          onChanged();
        }}
      />

      {detail && (
        <Dialog open onClose={() => setDetail(null)} fullWidth maxWidth="sm" scroll="paper">
          <DialogTitle>
            {detail.company_name}
            <Typography variant="body2" color="text.secondary">{detail.reference}</Typography>
          </DialogTitle>
          <DialogContent dividers>
            <Kv k="Status" v={STATUS[detail.status]?.label ?? detail.status} />
            <Kv k="Workspace ID" v={detail.tenant_key ?? detail.workspace_key} />
            <Kv k="Administrator" v={`${detail.admin_full_name} <${detail.admin_email}>`} />
            <Kv k="Username" v={detail.admin_username} />
            <Kv k="Method" v={detail.method === 'GOOGLE' ? 'Google' : 'Email + password'} />
            <Kv k="Email verified" v={detail.email_verified ? 'yes' : 'no'} />
            <Kv k="From" v={`${detail.requester_ip ?? '—'} · ${detail.user_agent ?? '—'}`} />
            <Kv k="Requested" v={fmtDate(detail.created_at)} />
            {detail.decided_by && (
              <Kv k="Decided" v={`${detail.decided_by} · ${fmtDate(detail.decided_at)}${detail.decision_reason ? ` · ${detail.decision_reason}` : ''}`} />
            )}
            {detail.error_summary && (
              <Alert severity="error" sx={{ mt: 1.5, wordBreak: 'break-word' }}>{detail.error_summary}</Alert>
            )}
            {!!detail.steps?.length && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" gutterBottom>Setup log</Typography>
                <Stack spacing={0.75}>
                  {detail.steps.map((s) => (
                    <Box key={s.key}>
                      <Typography variant="body2">
                        <strong>{s.status}</strong> · {s.label}
                      </Typography>
                      {(s.error || s.detail) && (
                        <Typography variant="caption" color={s.error ? 'error' : 'text.secondary'}
                          sx={{ wordBreak: 'break-word', display: 'block' }}>
                          {s.error ?? s.detail}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
}

function Kv({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, py: 0.4 }}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 130 }}>{k}</Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{v}</Typography>
    </Box>
  );
}
